import { createSelector } from 'reselect'
import { AppState } from '@chegg-wt/core/state'
import { Config } from '@chegg-wt/core/config'
import {
  MY_CITATION_PROJECT_LIST_TITLE,
  NEW_CITATION_LIST_BUTTON,
} from '@chegg-wt/core/constants/citations'
import {
  DASHBOARD,
  CITATIONS,
  PAPERS,
  GUIDES,
  PLAGIARISM,
  WRITING_ASSISTANT,
} from '@chegg-wt/core/constants/nav'
import { NavItem, NavItems } from '@chegg-wt/core/types/core'
import { currentProjectIdSelector } from '@chegg-wt/core/state/currentProject/selectors'
import { userEmailSelector, isProSelector } from '@chegg-wt/core/state/user/selectors'
import { supportPageLinks } from '@chegg-wt/core/constants/supportPageLinks'
import { isWritingAssistantHomeEnabledSelector } from '@chegg-wt/core-wa/state/selectors'
import { EB_PRODUCT_NAME } from '@chegg-wt/core/constants/site'

export const navItemsSelector: (state: AppState) => NavItems = createSelector(
  [
    isProSelector,
    userEmailSelector,
    currentProjectIdSelector,
    isWritingAssistantHomeEnabledSelector,
    (_state: AppState, isHome?: boolean) => isHome,
  ],
  (
    paidUser: boolean,
    email: string,
    currentProjectId: string,
    isWritingAssistantHomeEnabled: boolean,
    isHome?: boolean
  ) => {
    const isWritingAssistEnabledAndHomePage: boolean =
      isWritingAssistantHomeEnabled && isHome
    const getWritingAssistantNavItem = (): NavItem => {
      const isEbSite = Config.siteName === EB_PRODUCT_NAME
      let writingAssistantNavItem: NavItem = {} as any
      if (isEbSite && !isHome) {
        // only add WA link on non-home pages on EB site
        writingAssistantNavItem = {
          name: WRITING_ASSISTANT,
          route: 'toWritingAssistant',
          hideOnMobile: true,
          new: true,
          hideOnSmall: true,
          icon: 'citation-guides',
        }
      }
      return writingAssistantNavItem
    }
    const getCitationNavItem = (): NavItem => {
      const citationsItem: NavItem = {
        name: CITATIONS,
        hideOnSmall: true,
      }

      if (currentProjectId) {
        return {
          ...citationsItem,
          route: 'toProject',
        }
      }
      if (email) {
        return {
          ...citationsItem,
          route: 'toFolders',
        }
      }
      return {
        ...citationsItem,
        route: 'toSourceTypeSelection',
        query: {
          style: 'mla9',
        },
      }
    }
    const main: NavItem[] = [
      {
        name: DASHBOARD,
        route: 'toDashboard',
        hideOnSmall: true,
      },
      getCitationNavItem(),
      {
        name: 'Plagiarism and grammar',
        icon: 'citation-guides',
        href: '/grammar-and-plagiarism/',
      },
      getWritingAssistantNavItem(),
      {
        name: 'About us',
        icon: 'about-us',
        href: '/guides/company/',
      },
      {
        name: 'Support',
        icon: 'support',
        href: `${supportPageLinks.easybib}/?Website=External`,
      },
      {
        name: GUIDES,
        href: Config.guidesUrl,
        hideOnSmall: true,
      },
    ]

    let right: NavItem[] = []

    const account: NavItem[] = [
      {
        name: MY_CITATION_PROJECT_LIST_TITLE,
        icon: 'projects',
        route: 'toFolders',
      },
      {
        name: NEW_CITATION_LIST_BUTTON,
        icon: 'add',
        route: 'toFolders',
        query: {
          create: 'project',
        },
      },
      {
        name: 'My Account',
        icon: 'settings',
        route: 'toProfile',
      },
      {
        name: 'Logout',
        icon: 'logout',
        route: 'toLogout',
      },
    ]

    const getPapersNavItem = (): NavItem => {
      const papersItem: NavItem = {
        name: PAPERS,
        hideOnSmall: true,
      }

      if (email) {
        return {
          ...papersItem,
          href: Config.writingUrl,
        }
      }

      return {
        ...papersItem,
        href: Config.grammarAndPlagiarismUrl,
      }
    }

    const getPlagiarismNavItem = (): NavItem => {
      const plagiarismItem: NavItem = {
        name: PLAGIARISM,
        route: 'toPlagiarism',
        hideOnMobile: true,
      }
      return plagiarismItem
    }

    const header: NavItem[] = [
      {
        name: DASHBOARD,
        route: 'toDashboard',
        hideOnSmall: true,
      },
      getCitationNavItem(),
      isWritingAssistEnabledAndHomePage ? getPlagiarismNavItem() : getPapersNavItem(),
      getWritingAssistantNavItem(),
      {
        name: GUIDES,
        href: Config.guidesUrl,
        hideOnSmall: true,
      },
    ]

    if (!paidUser) {
      main.splice(3, 0, {
        name: 'Upgrade',
        icon: 'plus',
        route: 'toUpgrade',
      })

      right = [
        ...right,
        {
          name: 'Upgrade',
          route: 'toUpgrade',
          hideOnMobile: true,
        },
      ]

      account.splice(3, 0, {
        name: 'Upgrade',
        icon: 'plus',
        route: 'toUpgrade',
      })
    }

    if (!email) {
      right = [
        ...right,
        {
          name: 'Sign up',
          route: 'toSignup',
        },
        {
          name: 'Sign in',
          route: 'toLogin',
        },
      ]
    }

    const getWritingAssistantMain = (): NavItem[] => {
      if (!paidUser)
        return [
          {
            name: 'Upgrade',
            route: 'toUpgrade',
          },
        ]
      return []
    }

    return {
      main: isWritingAssistEnabledAndHomePage ? getWritingAssistantMain() : main,
      right,
      account,
      header: header.filter((navItem) => !!navItem?.name), // remove any {} or undefined objects
    }
  }
)
